import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Palette as PaletteIcon,
  Preview as PreviewIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { getBranding, updateBranding, BrandingConfig } from '../services/api';

interface BrandingConfigurationProps {
  tenantId?: string;
}

export default function BrandingConfiguration({ tenantId }: BrandingConfigurationProps) {
  const [branding, setBranding] = useState<BrandingConfig>({
    BackgroundColor: '#FFFFFF',
    HeaderBackgroundColor: '#1976d2',
    SignInPageText: '',
    UsernameHintText: '',
    CompanyLogoUrl: '',
    CustomAccountResetCredentialsUrl: '',
    CustomCannotAccessYourAccountText: '',
    CustomCannotAccessYourAccountUrl: '',
    CustomForgotMyPasswordText: '',
    CustomPrivacyAndCookiesText: '',
    CustomPrivacyAndCookiesUrl: '',
    CustomResetItNowText: '',
    CustomTermsOfUseText: '',
    CustomTermsOfUseUrl: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  useEffect(() => {
    if (tenantId) {
      loadBranding();
    }
  }, [tenantId]);

  const loadBranding = async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getBranding(tenantId);
      
      // Check if branding has been configured (any non-default values exist)
      const hasExistingBranding = data.BackgroundColor || data.HeaderBackgroundColor || 
                                   data.SignInPageText || data.UsernameHintText;
      setHasSaved(!!hasExistingBranding);
      
      // Merge with defaults
      setBranding({
        BackgroundColor: data.BackgroundColor || '#FFFFFF',
        HeaderBackgroundColor: data.HeaderBackgroundColor || '#1976d2',
        SignInPageText: data.SignInPageText || '',
        UsernameHintText: data.UsernameHintText || '',
        CompanyLogoUrl: data.CompanyLogoUrl || '',
        CustomAccountResetCredentialsUrl: data.CustomAccountResetCredentialsUrl || '',
        CustomCannotAccessYourAccountText: data.CustomCannotAccessYourAccountText || '',
        CustomCannotAccessYourAccountUrl: data.CustomCannotAccessYourAccountUrl || '',
        CustomForgotMyPasswordText: data.CustomForgotMyPasswordText || '',
        CustomPrivacyAndCookiesText: data.CustomPrivacyAndCookiesText || '',
        CustomPrivacyAndCookiesUrl: data.CustomPrivacyAndCookiesUrl || '',
        CustomResetItNowText: data.CustomResetItNowText || '',
        CustomTermsOfUseText: data.CustomTermsOfUseText || '',
        CustomTermsOfUseUrl: data.CustomTermsOfUseUrl || '',
      });
    } catch (err: any) {
      console.error('Failed to load branding:', err);
      // Not an error if branding doesn't exist yet - use defaults
      if (err.response?.status !== 404) {
        setError(err.response?.data?.error || 'Failed to load branding configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!tenantId) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Convert to API format (camelCase) - backend expects camelCase properties
      const payload = {
        backgroundColor: branding.BackgroundColor,
        headerBackgroundColor: branding.HeaderBackgroundColor,
        signInPageText: branding.SignInPageText,
        usernameHintText: branding.UsernameHintText,
        companyLogoUrl: branding.CompanyLogoUrl,
        customAccountResetCredentialsUrl: branding.CustomAccountResetCredentialsUrl,
        customCannotAccessYourAccountText: branding.CustomCannotAccessYourAccountText,
        customCannotAccessYourAccountUrl: branding.CustomCannotAccessYourAccountUrl,
        customForgotMyPasswordText: branding.CustomForgotMyPasswordText,
        customPrivacyAndCookiesText: branding.CustomPrivacyAndCookiesText,
        customPrivacyAndCookiesUrl: branding.CustomPrivacyAndCookiesUrl,
        customResetItNowText: branding.CustomResetItNowText,
        customTermsOfUseText: branding.CustomTermsOfUseText,
        customTermsOfUseUrl: branding.CustomTermsOfUseUrl,
        loginPageLayoutConfiguration: branding.LoginPageLayoutConfiguration,
        loginPageTextVisibilitySettings: branding.LoginPageTextVisibilitySettings,
      };

      // Send camelCase payload directly to API
      await updateBranding(tenantId, payload as any);
      setSuccess(true);
      setHasSaved(true);
      
      // Reload the page after a short delay to show the success message
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save branding configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof BrandingConfig) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setBranding({
      ...branding,
      [field]: event.target.value,
    });
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <PaletteIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" gutterBottom>
            Organizational Branding
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Customize your sign-in page and portal appearance
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(false)}>
          Branding configuration saved successfully! Changes will be applied to your M365 tenant
          during the next policy deployment.
        </Alert>
      )}

      {/* Info Banner */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>What is this?</strong>
        </Typography>
        <Typography variant="body2">
          Configure your organization's branding colors and text. These settings will:
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
          <li>Customize your Customer Portal experience with your brand colors</li>
          <li>
            Deploy to your Microsoft 365 tenant sign-in page during policy deployment (requires
            a Service Plan with Entra ID P1 or P2)
          </li>
        </Box>
      </Alert>

      {/* Color Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <PaletteIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Color Scheme
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Background Color
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  fullWidth
                  type="color"
                  value={branding.BackgroundColor}
                  onChange={handleChange('BackgroundColor')}
                  sx={{ width: 100 }}
                />
                <TextField
                  fullWidth
                  value={branding.BackgroundColor}
                  onChange={handleChange('BackgroundColor')}
                  placeholder="#FFFFFF"
                  helperText="Hex color for sign-in page background"
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Header Background Color
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  fullWidth
                  type="color"
                  value={branding.HeaderBackgroundColor}
                  onChange={handleChange('HeaderBackgroundColor')}
                  sx={{ width: 100 }}
                />
                <TextField
                  fullWidth
                  value={branding.HeaderBackgroundColor}
                  onChange={handleChange('HeaderBackgroundColor')}
                  placeholder="#1976d2"
                  helperText="Hex color for header/banner background"
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Preview */}
        <Box mt={3}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Hide Preview' : 'Show Color Preview'}
          </Button>

          {showPreview && (
            <Card sx={{ mt: 2, border: '2px solid #ddd' }}>
              <Box
                sx={{
                  height: 60,
                  backgroundColor: branding.HeaderBackgroundColor,
                  display: 'flex',
                  alignItems: 'center',
                  px: 3,
                }}
              >
                <Typography variant="h6" sx={{ color: '#fff' }}>
                  Your Company Header
                </Typography>
              </Box>
              <Box
                sx={{
                  height: 200,
                  backgroundColor: branding.BackgroundColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    minWidth: 300,
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Sign In Preview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {branding.SignInPageText || 'Welcome! Please sign in to continue.'}
                  </Typography>
                </Paper>
              </Box>
            </Card>
          )}
        </Box>
      </Paper>

      {/* Sign-In Page Text */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Sign-In Page Text
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Sign-In Page Text"
              value={branding.SignInPageText}
              onChange={handleChange('SignInPageText')}
              placeholder="Welcome! Enjoy your day!"
              helperText="Custom welcome message displayed on the sign-in page"
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Username Hint Text"
              value={branding.UsernameHintText}
              onChange={handleChange('UsernameHintText')}
              placeholder="Enter your email address"
              helperText="Placeholder text shown in the username input field"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Advanced Customization */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Advanced Customization</Typography>
          <Chip label="Optional" size="small" sx={{ ml: 2 }} />
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Account Reset */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                Account Reset Credentials
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Reset URL"
                value={branding.CustomAccountResetCredentialsUrl}
                onChange={handleChange('CustomAccountResetCredentialsUrl')}
                placeholder="https://yourcompany.com/accountreset"
                helperText="URL for account reset page"
              />
            </Grid>

            {/* Can't Access Account */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                Can't Access Your Account
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Custom Text"
                value={branding.CustomCannotAccessYourAccountText}
                onChange={handleChange('CustomCannotAccessYourAccountText')}
                placeholder="Can't access your account? No worries, click the link below."
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Help URL"
                value={branding.CustomCannotAccessYourAccountUrl}
                onChange={handleChange('CustomCannotAccessYourAccountUrl')}
                placeholder="https://yourcompany.com/accounts/help"
              />
            </Grid>

            {/* Forgot Password */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                Forgot Password
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Forgot Password Text"
                value={branding.CustomForgotMyPasswordText}
                onChange={handleChange('CustomForgotMyPasswordText')}
                placeholder="Forgot your password? No worries, happens to all of us."
                multiline
                rows={2}
              />
            </Grid>

            {/* Reset It Now */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reset It Now Text"
                value={branding.CustomResetItNowText}
                onChange={handleChange('CustomResetItNowText')}
                placeholder="I know what I am doing. Click here to reset your password."
                multiline
                rows={2}
              />
            </Grid>

            {/* Privacy & Cookies */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                Privacy & Cookies
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Privacy & Cookies Text"
                value={branding.CustomPrivacyAndCookiesText}
                onChange={handleChange('CustomPrivacyAndCookiesText')}
                placeholder="We use cookies to make your experience better."
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Privacy Policy URL"
                value={branding.CustomPrivacyAndCookiesUrl}
                onChange={handleChange('CustomPrivacyAndCookiesUrl')}
                placeholder="https://yourcompany.com/privacy"
              />
            </Grid>

            {/* Terms of Use */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                Terms of Use
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Terms of Use Text"
                value={branding.CustomTermsOfUseText}
                onChange={handleChange('CustomTermsOfUseText')}
                placeholder="Without lawyers this would be a lot shorter."
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Terms of Use URL"
                value={branding.CustomTermsOfUseUrl}
                onChange={handleChange('CustomTermsOfUseUrl')}
                placeholder="https://yourcompany.com/terms"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Save Button */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={loadBranding} disabled={saving}>
          Reset
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !tenantId}
        >
          {saving ? 'Saving...' : (hasSaved ? 'Branded, Save a New Brand' : 'Save Branding Configuration')}
        </Button>
      </Box>

      {/* Deployment Notice */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Branding changes will be applied to your Microsoft 365 tenant
          during your next policy deployment. To apply changes immediately, trigger a new
          deployment from the Deploy Config page.
        </Typography>
      </Alert>
    </Container>
  );
}
